import {
  Briefcase,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  Globe,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { formatDate } from "../../Components/Common/FormatDate";
import StudentFormService from "../../services/KangarooServices/StudentFormService";
import { useParams } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import JobApplicationService from "../../services/KangarooServices/JobApplicationService";
import { imageBaseApi } from "../../utilis/imageHelper";

const JobAppDetail = () => {
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const { id } = useParams();

  const fetchDetail = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await JobApplicationService.getDetail(id);
      setApplication(result);
    } catch (error) {
      console.error("Error fetching job application:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail(id);
  }, [id]);

  const getStatusColor = (status) => {
    const map = {
      New: "info",
      Applied: "primary",
      Reviewed: "warning",
      Interview: "secondary",
      Rejected: "danger",
      Hired: "success",
    };
    return map[status] || "secondary";
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container
          fluid
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner color="primary" size="lg" />
        </Container>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Job Application Detail"
            pageTitle="Job Applications"
          />
          <Card>
            <CardBody className="text-center py-5">
              <h5>Application not found</h5>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Application Details"
          pageTitle="Job Applications"
          breadcrumbItems={[
            { title: "Back to List", link: "/job-application" },
          ]}
        />

        <Row>
          <Col lg={12}>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <User size={32} className="me-3 text-primary" />
                    <div>
                      <h3 className="mb-1">{application.fullName}</h3>
                      <p className="text-muted mb-0">
                        Applying for{" "}
                        <strong>{application.jobListingPosition}</strong>
                      </p>
                    </div>
                  </div>
                  <Badge
                    color={getStatusColor(application.jobApplicationStatus)}
                    pill
                    className="px-4 py-2 fs-6"
                  >
                    {application.jobApplicationStatus}
                  </Badge>
                </div>
              </CardHeader>

              <CardBody className="pt-4">
                {/* Personal & Contact Info */}
                <Row className="g-4 mb-5">
                  <Col md={6}>
                    <h5 className="mb-4 text-primary">
                      <Mail size={20} className="me-2" />
                      Contact Information
                    </h5>
                    <div className="ps-4">
                      <p className="mb-2">
                        <strong>Email:</strong>{" "}
                        <a href={`mailto:${application.email}`}>
                          {application.email}
                        </a>
                      </p>
                      <p className="mb-2">
                        <Phone size={16} className="me-2 text-muted" />
                        <strong>Phone:</strong> {application.phoneNumber}
                      </p>
                    </div>
                  </Col>

                  <Col md={6}>
                    <h5 className="mb-4 text-primary">
                      <MapPin size={20} className="me-2" />
                      Address
                    </h5>
                    <div className="ps-4">
                      <p className="mb-2">
                        <strong>Permanent:</strong>{" "}
                        {application.permanentAddress || "N/A"}
                      </p>
                      <p className="mb-2">
                        <strong>Local:</strong>{" "}
                        {application.localAddress || "N/A"}
                      </p>
                    </div>
                  </Col>
                </Row>

                {/* Job & Resume */}
                <Row className="g-4 mb-5">
                  <Col md={6}>
                    <h5 className="mb-4 text-primary">
                      <Briefcase size={20} className="me-2" />
                      Applied Position
                    </h5>
                    <div className="ps-4">
                      <p className="mb-0 fs-5">
                        {application.jobListingPosition}
                      </p>
                    </div>
                  </Col>

                  <Col md={6}>
                    <h5 className="mb-4 text-primary">
                      <FileText size={20} className="me-2" />
                      Resume
                    </h5>
                    <div className="ps-4">
                      {application.resumeUrl ? (
                        <Button
                          color="primary"
                          tag="a"
                          href={`${imageBaseApi}/${application.resumeUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-inline-flex align-items-center"
                        >
                          <Download size={16} className="me-2" />
                          Download Resume
                        </Button>
                      ) : (
                        <span className="text-muted">No resume uploaded</span>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Timestamps */}
                <Row className="g-4">
                  <Col md={6}>
                    <h5 className="mb-4 text-primary">
                      <Clock size={20} className="me-2" />
                      Timeline
                    </h5>
                    <div className="ps-4">
                      <p className="mb-2">
                        <strong>Applied on:</strong>{" "}
                        {formatDate(application.createdAt)}
                      </p>
                      <p className="mb-0">
                        <strong>Last updated:</strong>{" "}
                        {application.updatedAt
                          ? formatDate(application.updatedAt)
                          : "Not updated yet"}
                      </p>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="border-start ps-4 text-muted small">
                      <p className="mb-1">
                        <strong>Application ID:</strong> {application.id}
                      </p>
                      <p className="mb-0">
                        <strong>Job Listing ID:</strong>{" "}
                        {application.jobListingId}
                      </p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default JobAppDetail;
